import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ToastService } from '../../core/services/toast.service';

/**
 * The one host for ToastService.
 *
 * It used to live inside TopbarComponent's template. That component only renders
 * in the LEGACY shell — app.html picks `<app-topbar-v3 />` whenever
 * enableHorizontalNav() is on — so in the horizontal shell nothing was
 * subscribed to the signal and every toast was set and then silently dropped.
 * That took out more than the grids' "Documents — coming soon": the auth
 * interceptor reports session and request failures the same way, and those were
 * invisible too.
 *
 * Its own component, rendered once at the root outside both shell branches, so
 * neither shell can own it and neither can lose it. Nothing here depends on
 * where it sits in the DOM — the toast is position:fixed.
 */
@Component({
  selector: 'app-global-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (toastSvc.toast(); as t) {
      <div class="global-toast" [attr.data-type]="t.type"
           role="alert" title="Dismiss" (click)="toastSvc.dismiss()">{{ t.message }}</div>
    }
  `,
  styles: [`
    /* Geometry and type copied from /products' .ps-toast, so the same action
       produces the same object on every page. It was a light bordered card with
       an icon and a close button — a second toast vocabulary for the same job.
       The icon and the ✕ went with it: the pill is click-to-dismiss and the
       service auto-hides, so neither earned its width. */
    .global-toast {
      position: fixed;
      /* 80, not 24: /products clears the bottom BASKET bar, and a toast that
         sits at a different height on each page is still two toasts. */
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      max-width: 520px;
      padding: 8px 20px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.4;
      text-align: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      animation: toast-in 0.2s ease-out;

      background: #0D2A45;
      color: #fff;
    }

    /* Severity survives the restyle as the pill's colour rather than as an
       icon. Same two hues the bordered card used for its text, filled instead
       of tinted, so an error still cannot be mistaken for a notice — this is
       the surface the auth interceptor reports failures on. */
    .global-toast[data-type='error'] { background: #991b1b; }
    .global-toast[data-type='warn']  { background: #92400e; }

    @media (prefers-reduced-motion: reduce) {
      .global-toast { animation: none; }
    }

    @keyframes toast-in {
      from { opacity: 0; transform: translateX(-50%) translateY(10px); }
      to   { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
  `],
})
export class GlobalToastComponent {
  readonly toastSvc = inject(ToastService);
}
